import type * as Fabric from '@postermywall/fabricjs-2';
import type {PageItemsObject} from '@PosterWhiteboard/page/page-items.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';

import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';

export const PAGE_OPTIONS_WIDTH = 34;
export const MAX_ALLOWED_GRAPHIC_ITEMS = 300;
export const PAGE_SLIDER_CONTAINER_HEIGHT = 14;

/**
 * Initial duration assigned to a background being inserted into an empty page
 * (no other backgrounds, no items). When items already exist, the first
 * background is sized to cover them instead. See `buildColorBackgroundItemObject`.
 */
export const DEFAULT_BACKGROUND_DURATION = 5;

export interface PageModifiedEvent {
  firePosterModifiedEvent: boolean;
}

export interface PageEvents {
  modified: PageModifiedEvent;
  'time:updated': number;
  'playback:ended': void;
  seek: number;
}

export interface ToDataURLOpts {
  maxDimension?: number;
  width?: number;
  height?: number;
}

export interface PageUpdateFromObjectOpts extends UpdateFromObjectOpts {
  firePosterModifiedEvent?: boolean;
}

export interface TemplateDataObject {
  posterId: string;
  pageId: string;
}
export interface GalleryTemplateDataObject {
  posterId: string;
  pageId: string;
  idGalleryTemplateCreator?: number;
}

export interface PageObject {
  hashedID: string;
  items: PageItemsObject;
  audioClips: AudioClipsObject;
  isCustomDuration: boolean;
  zoom: number;
  isPlaying: boolean;
  templateData?: TemplateDataObject;
  galleryTemplateData?: GalleryTemplateDataObject;
  currentTime: number;
  appliedPalette?: ColorPaletteInfo;
  lastModified: Date;
}

export const CANVAS_BACKDROP_CLASS = 'pmw-canvas-backdrop';
export const PAGE_CONTAINER_CLASS = 'pmw-page-container';
export const INNER_PAGE_CONTAINER_CLASS = 'inner-page-container';
export const ACTIVE_PAGE_FLASH_CLASS = 'pmw-page-active-flash';
export const EMPTY_BACKGROUND_HOVER_CLASS = 'pmw-empty-background-hover';
// Minimum pointer travel (client px) from mousedown before a gesture counts as a marquee
// drag-select rather than a click. Fabric's own isClick flips false on a single sub-pixel
// move, so the editor keys drag detection off this threshold instead.
export const MARQUEE_DRAG_THRESHOLD_PX = 3;
export const TRANSPARENT_CANVAS_COLOR = '#00000000';
export const OPAQUE_CANVAS_COLOR = '#ffffff';

export interface ContextMenuStoppedEventData {
  e: MouseEvent;
  target: Fabric.Object | undefined;
}

export const DEFAULT_BACKGROUND_COLOR = 'ffffff';
export const DEFAULT_SECONDARY_BACKGROUND_COLOR = '000000';
export const MAX_ITEM_ANIMATION_DELAY_FACTOR = 0.3; // Remember to change its counter part in php postervo variable value too
export const MAX_ANIMATION_DURATION = 4;
export const FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR = 0.4;
export const MAX_ITEMS_WITH_FIXED_DELAY_FACTOR = 5;
export const GUIDELINES_COLOR = '#FF5177';
export const GUIDELINES_MARGIN = 3;

// Backgrounds overflow the canvas by 1px on every side to hide sub-pixel seams.
// FULL_PAGE_OFFSET is the left/top position; FULL_PAGE_PADDING is the matching width/height delta
// (== -2 * FULL_PAGE_OFFSET); FULL_PAGE_OVERFLOW is the per-side overflow used by scale-based sizing.
export const FULL_PAGE_OFFSET = -1;
export const FULL_PAGE_PADDING = 2;
export const FULL_PAGE_OVERFLOW = 1;
